import React, { useMemo } from "react";
import { Card, CardBody, CardHeader } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { formatCurrency } from "../../utils/formatCurrency";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Aca el concepto SI se conoce, asi que el color se pide por concepto y no se
// escribe a mano: las cuatro modalidades llevan la rampa del azul de Tickets
// —son la misma familia, subdividida por duracion— y abonados, acuerdos e
// ingresos manuales llevan su color de origen.
import { MODALIDAD, ORIGEN } from "../Stats2/theme";
import { LEYENDA_DONA } from "./graficos";

const RevenueBreakdown = ({ dailyDocs, period = 30 }) => {
  const { labels, series, colors } = useMemo(() => {
    // Use last N days
    const recent = [...dailyDocs]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, period);

    const hourAmount = recent.reduce((a, d) => a + (d.tickets?.hour?.amount || 0), 0);
    const stayAmount = recent.reduce((a, d) => a + (d.tickets?.stay?.amount || 0), 0);
    const tnAmount = recent.reduce((a, d) => a + (d.tickets?.tn?.amount || 0), 0);
    const planaAmount = recent.reduce((a, d) => a + (d.tickets?.plana?.amount || 0), 0);
    const monthlyAmount = recent.reduce((a, d) => a + (d.monthly?.amount || 0), 0);
    const dealsAmount = recent.reduce((a, d) => a + (d.deals?.amount || 0), 0);
    const manualAmount = recent.reduce((a, d) => a + (d.manual?.amount || 0), 0);

    // Las cuatro primeras son MODALIDADES DE COBRO del ticket, no orígenes
    // distintos: por eso arrancan con "Tickets cobrados". "Estadía" a secas
    // parecía contestar cuánto se queda la gente, que es otra pregunta.
    const items = [
      { label: "Tickets cobrados por hora", value: hourAmount, color: MODALIDAD.hour.color },
      { label: "Tickets cobrados por estadía", value: stayAmount, color: MODALIDAD.stay.color },
      { label: "Tickets cobrados por turno", value: tnAmount, color: MODALIDAD.tn.color },
      { label: "Tickets con tarifa plana", value: planaAmount, color: MODALIDAD.plana.color },
      { label: "Abonados", value: monthlyAmount, color: ORIGEN.abonados.color },
      { label: "Acuerdos", value: dealsAmount, color: ORIGEN.convenios.color },
      { label: "Ingresos manuales", value: manualAmount, color: ORIGEN.otros.color },
    ].filter((i) => i.value > 0);

    return {
      labels: items.map((i) => i.label),
      series: items.map((i) => i.value),
      colors: items.map((i) => i.color),
    };
  }, [dailyDocs, period]);

  const options = useMemo(
    () => ({
      chart: { type: "donut", fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      labels,
      colors,
      // Siete orígenes de plata que no aparecen en ningún eje: la leyenda es
      // lo único que los nombra.
      legend: LEYENDA_DONA,
      tooltip: {
        y: { formatter: (v) => formatCurrency(v, true) },
      },
      plotOptions: {
        pie: {
          donut: {
            size: "60%",
            labels: {
              show: true,
              value: {
                fontSize: "14px",
                fontWeight: 600,
                formatter: (val) => formatCurrency(Number(val), true),
              },
              total: {
                show: true,
                label: "Total",
                fontSize: "13px",
                formatter: (w) => {
                  const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                  return formatCurrency(total, true);
                },
              },
            },
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => `${val.toFixed(0)}%`,
        style: { fontSize: "11px" },
      },
    }),
    [labels, colors]
  );

  if (!series.length) return null;

  return (
    <Card className="shadow-sm h-100">
      <CardHeader className="bg-white py-3">
        <h6 className="mb-0" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>
            pie_chart
          </span>
          De dónde viene tu plata
          <span className="ms-1" style={TITULO_ACLARACION}>
            (últimos {period} días)
          </span>
        </h6>
      </CardHeader>
      <CardBody>
        <ReactApexChart options={options} series={series} type="donut" height={320} />
      </CardBody>
    </Card>
  );
};

export default RevenueBreakdown;
